import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, Search, Image as ImageIcon, X, Save, Upload, Loader2, RefreshCw, Check, ShoppingCart, Box } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import ConfirmModal from '@/components/ui/ConfirmModal';

// Helper para obtener URL Pública
const getImageUrl = (filename) => {
    if (!filename) return '';
    const { data } = supabase.storage.from('plasa_images').getPublicUrl(`productos/${filename}`);
    return data.publicUrl;
};

// Componente Tarjeta Vista Previa Pública
const PublicProductCard = ({ item, onEdit, onDelete }) => {
    // Normalizar datos para la vista (la estructura pública es plana, la de admin tiene joins)
    const product = {
        producto: item.producto?.producto || 'Sin Nombre',
        categoria: item.producto?.categoria?.categoria,
        color: item.color?.color,
        hexadecimal: item.color?.hexadecimal,
        textura: item.textura?.textura,
        material: item.material?.material,
        imagen: item.imagen,
        textura_imagen: item.textura_imagen,
        disenada_para: item.disenada_para?.disenada_para,
        presentacion: item.presentacion?.presentacion,
        base: item.base?.base,
        diametro: item.diametro?.diametro
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            layout
            className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-red-100 flex flex-col relative"
        >
            {/* Admin Controls Overlay */}
            <div className="absolute top-2 right-2 z-20 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={(e) => { e.stopPropagation(); onEdit(item); }} className="p-2 bg-white/90 backdrop-blur text-zinc-700 rounded-lg shadow-md hover:text-blue-600 transition-colors border border-gray-200"><Edit2 size={16} /></button>
                <button onClick={(e) => { e.stopPropagation(); onDelete(item.id); }} className="p-2 bg-white/90 backdrop-blur text-zinc-700 rounded-lg shadow-md hover:text-red-600 transition-colors border border-gray-200"><Trash2 size={16} /></button>
            </div>

            {/* Imagen Púbilca Style */}
            <div className="relative w-full aspect-[4/3] bg-white overflow-hidden flex items-center justify-center rounded-t-xl group-hover:bg-gray-50/30 transition-colors">
                {product.imagen ? (
                    <img
                        src={getImageUrl(product.imagen)}
                        alt={product.producto}
                        className="w-full h-full object-contain hover:scale-105 transition-transform duration-500 p-4"
                        loading="lazy"
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center text-gray-300">
                        <Box size={48} strokeWidth={1.5} />
                        <span className="text-xs mt-2 font-medium">Sin Imagen</span>
                    </div>
                )}

                {/* Miniatura Textura (si existe) */}
                {product.textura_imagen && (
                    <div className="absolute bottom-2 right-2 w-10 h-10 rounded-full overflow-hidden border-2 border-white shadow-md z-10">
                        <img
                            src={`/images/Texturas/${product.textura_imagen}`}
                            alt="textura"
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}
            </div>

            {/* Info Body Styled Like Public */}
            <div className="flex-1 flex flex-col p-5 justify-between bg-white relative">
                <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 leading-tight">
                        {product.producto}
                    </h3>
                    {product.categoria && (
                        <span className="inline-block px-3 py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full">
                            {product.categoria}
                        </span>
                    )}
                </div>

                {/* Detalles Clave */}
                <div className="mt-4 space-y-2 text-sm text-gray-600">
                    {product.color && (
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 font-medium w-16 text-xs uppercase tracking-wide">Color:</span>
                            <div className="flex items-center gap-2">
                                <div
                                    className="w-4 h-4 rounded-full border border-gray-200 shadow-sm"
                                    style={{ backgroundColor: product.hexadecimal || '#ccc' }}
                                />
                                <span className="text-gray-700 font-medium">{product.color}</span>
                            </div>
                        </div>
                    )}
                    {product.textura && (
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 font-medium w-16 text-xs uppercase tracking-wide">Textura:</span>
                            <span className="text-gray-700">{product.textura}</span>
                        </div>
                    )}
                    {product.material && (
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 font-medium w-16 text-xs uppercase tracking-wide">Material:</span>
                            <span className="text-gray-700">{product.material}</span>
                        </div>
                    )}
                    {product.disenada_para && (
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 font-medium w-16 text-xs uppercase tracking-wide">Para:</span>
                            <span className="text-gray-700">{product.disenada_para}</span>
                        </div>
                    )}
                    {product.base && (
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 font-medium w-16 text-xs uppercase tracking-wide">Base:</span>
                            <span className="text-gray-700">{product.base}</span>
                        </div>
                    )}
                    {product.diametro && (
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 font-medium w-16 text-xs uppercase tracking-wide">Diám:</span>
                            <span className="text-gray-700">{product.diametro}</span>
                        </div>
                    )}
                    {product.presentacion && (
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 font-medium w-16 text-xs uppercase tracking-wide">Pres:</span>
                            <span className="text-gray-700">{product.presentacion}</span>
                        </div>
                    )}
                </div>

                {/* Botón CTA (Visual) */}
                <div className="mt-6 pt-4 border-t border-gray-50">
                    <div className="w-full py-2.5 px-4 rounded-lg bg-gradient-to-r from-red-600 to-rose-700 text-white font-medium text-sm shadow-lg shadow-red-900/20 flex items-center justify-center gap-2 opacity-90 grayscale-[30%] cursor-default">
                        <ShoppingCart size={16} />
                        Solicitar Información
                    </div>
                    <p className="text-[10px] text-center text-zinc-400 mt-2 font-mono">Vista Previa Pública</p>
                </div>
            </div>
        </motion.div>
    );
};

// Componente Selector de Imagen Visual
const ImageSelector = ({ value, onChange }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);



    useEffect(() => {
        fetchImages();
    }, []);

    const fetchImages = async () => {
        setLoading(true);
        const { data, error } = await supabase.storage.from('plasa_images').list('productos', {
            limit: 100,
            offset: 0,
            sortBy: { column: 'created_at', order: 'desc' },
        });
        if (data) setImages(data);
        setLoading(false);
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
            const filePath = `productos/${fileName}`;

            const { error } = await supabase.storage.from('plasa_images').upload(filePath, file);
            if (error) throw error;

            await fetchImages();
            onChange(fileName);
        } catch (error) {
            console.error('Upload error:', error);
            alert('Error al subir imagen');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteImage = (e, imageName) => {
        e.stopPropagation();
        setConfirmConfig({
            isOpen: true,
            title: 'Eliminar Imagen',
            message: `¿Estás seguro de que deseas eliminar permanentemente la imagen "${imageName}"?`,
            onConfirm: () => executeDeleteImage(imageName)
        });
    };

    const executeDeleteImage = async (imageName) => {
        try {
            const { error } = await supabase.storage.from('plasa_images').remove([`productos/${imageName}`]);

            if (error) throw error;

            if (value === imageName) {
                onChange('');
            }

            await fetchImages();
        } catch (error) {
            console.error('Delete error:', error);
            alert('Error al eliminar la imagen del servidor');
        }
    };

    const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });

    return (
        <div className="w-full mb-8">
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 font-bold">Imagen Principal del Producto</label>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Main Preview */}
                <div className="lg:w-1/3">
                    <div className="relative w-full aspect-square bg-gray-100 dark:bg-zinc-950 rounded-xl border-2 border-dashed border-gray-300 dark:border-zinc-800 flex items-center justify-center overflow-hidden group shadow-sm">
                        {value ? (
                            <img
                                src={getImageUrl(value)}
                                alt="Preview"
                                className="w-full h-full object-contain p-2"
                            />
                        ) : (
                            <div className="text-center text-zinc-400 p-4">
                                <ImageIcon size={48} className="mx-auto mb-2 opacity-50" />
                                <p className="text-sm">Selecciona o sube una imagen</p>
                            </div>
                        )}

                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <label className="cursor-pointer bg-white text-zinc-900 px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-gray-100 transition-colors shadow-lg transform active:scale-95">
                                <Upload size={18} />
                                {uploading ? 'Subiendo...' : 'Subir Nueva'}
                                <input type="file" className="hidden" accept="image/*" onChange={handleUpload} disabled={uploading} />
                            </label>
                        </div>
                    </div>
                    {value && <p className="text-center text-xs text-zinc-400 mt-2 font-mono truncate">{value}</p>}
                </div>

                {/* Gallery Grid */}
                <div className="lg:w-2/3 flex flex-col">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Galería Disponible ({images.length})</span>
                        <button type="button" onClick={fetchImages} className="text-xs text-blue-500 hover:text-blue-400 flex items-center gap-1 font-medium"><RefreshCw size={12} /> Actualizar Lista</button>
                    </div>

                    <div className="flex-1 bg-gray-50 dark:bg-zinc-950/50 rounded-xl border border-gray-200 dark:border-zinc-800 p-3 overflow-y-auto max-h-[300px]">
                        {loading ? (
                            <div className="h-full flex items-center justify-center text-zinc-500"><Loader2 className="animate-spin" size={24} /></div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                                {images.map((img) => (
                                    <div
                                        key={img.name}
                                        onClick={() => onChange(img.name)}
                                        className={`aspect-square rounded-lg overflow-hidden cursor-pointer border-2 relative group transition-all ${value === img.name ? 'border-red-600 ring-2 ring-red-600/20 shadow-md' : 'border-transparent hover:border-zinc-300 dark:hover:border-zinc-600'}`}
                                        title={img.name}
                                    >
                                        <img src={getImageUrl(img.name)} className="w-full h-full object-cover" loading="lazy" />

                                        {/* Overlay de Selección */}
                                        {value === img.name && (
                                            <div className="absolute inset-0 bg-red-600/20 flex items-center justify-center backdrop-blur-[1px]">
                                                <div className="bg-red-600 rounded-full p-1 shadow-sm"><Check size={12} className="text-white" /></div>
                                            </div>
                                        )}

                                        {/* Botón de Eliminar (Trash) */}
                                        <button
                                            type="button"
                                            onClick={(e) => handleDeleteImage(e, img.name)}
                                            className="absolute top-1 right-1 p-1.5 bg-white/90 dark:bg-zinc-900/90 text-red-600 rounded-md shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 dark:hover:bg-red-950/50 z-20"
                                            title="Eliminar imagen del servidor"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={confirmConfig.isOpen}
                onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
            />
        </div>
    );
};

// Componente de Formulario para Crear/Editar Producto
const ProductForm = ({ item, catalogs, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        producto_id: item?.producto_id || '',
        categoria_id: item?.categoria_id || '', // Para filtrar productos (opcional, pero útil)
        color_id: item?.color_id || '',
        textura_id: item?.textura_id || '',
        material_id: item?.material_id || '',
        base_id: item?.base_id || '',
        presentacion_id: item?.presentacion_id || '',
        diametro_id: item?.diametro_id || '',
        disenada_para_id: item?.disenada_para_id || '',
        imagen: item?.imagen || '',
        textura_imagen: item?.textura_imagen || '',
        estado: item?.estado ?? true
    });



    // Helpers para inputs select
    const SelectField = ({ label, field, options, displayKey = 'nombre' }) => (
        <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1 font-bold">{label}</label>
            <select
                value={formData[field]}
                onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                className="w-full bg-gray-50 dark:bg-zinc-950 border border-gray-300 dark:border-zinc-800 rounded-lg px-3 py-2 text-zinc-900 dark:text-white focus:border-red-600 outline-none transition-colors"
            >
                <option value="">-- Seleccionar --</option>
                {options.map((opt, idx) => (
                    <option key={opt.id || idx} value={opt.id}>
                        {opt[displayKey] || opt.nombre || opt.color || opt.textura || opt.material || opt.producto}
                    </option>
                ))}
            </select>
        </div>
    );

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="space-y-4">

            {/* Image Selector Section - Prominent at top */}
            <ImageSelector
                value={formData.imagen}
                onChange={(val) => setFormData({ ...formData, imagen: val })}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SelectField label="Producto (Padre)" field="producto_id" options={catalogs.producto} displayKey="producto" />
                <SelectField label="Color" field="color_id" options={catalogs.color} displayKey="color" />
                <SelectField label="Textura" field="textura_id" options={catalogs.textura} displayKey="textura" />
                <SelectField label="Material" field="material_id" options={catalogs.material} displayKey="material" />
                <SelectField label="Base" field="base_id" options={catalogs.base} displayKey="base" />
                <SelectField label="Presentación" field="presentacion_id" options={catalogs.presentacion} displayKey="presentacion" />
                <SelectField label="Diámetro" field="diametro_id" options={catalogs.diametro} displayKey="diametro" />
                <SelectField label="Diseñada Para" field="disenada_para_id" options={catalogs.disenada_para} displayKey="disenada_para" />
            </div>

            {/* Hexadecimal removed: managed by color catalog */}

            <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors">Cancelar</button>
                <button type="submit" className="bg-red-600 hover:bg-red-500 text-white px-6 py-2 rounded-lg font-bold shadow-lg shadow-red-900/20">
                    Guardar Producto
                </button>
            </div>
        </form >
    );
};

const ProductManager = () => {
    const [products, setProducts] = useState([]);
    const [catalogs, setCatalogs] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });
    const { toast } = useToast();
    const BUCKET_URL = "https://tu-url-supabase.supabase.co/storage/v1/object/public/plasa_images/productos/";
    // NOTA: Ajustar la URL base si es necesario, o usar URL completa.
    // La app original parece concatenar path. Asumiré que necesitamos solo el filename.

    // Fetch initial data
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // 1. Cargar catálogos
            const tables = ['producto', 'color', 'textura', 'material', 'base', 'presentacion', 'diametro', 'disenada_para'];
            const catalogData = {};

            await Promise.all(tables.map(async (table) => {
                const { data } = await supabase.from(table).select('*').order('id');
                catalogData[table] = data || [];
            }));
            setCatalogs(catalogData);

            // 2. Cargar Producto Detalles con Joins
            const { data, error } = await supabase
                .from('producto_detalle')
                .select(`
                    *,
                    producto:producto_id(
                        producto,
                        categoria:categoria_id(categoria)
                    ),
                    color:color_id(color, hexadecimal),
                    textura:textura_id(textura),
                    material:material_id(material),
                    disenada_para:disenada_para_id(disenada_para),
                    base:base_id(base),
                    presentacion:presentacion_id(presentacion),
                    diametro:diametro_id(diametro)
                `)
                .order('id', { ascending: false }); // Más recientes primero

            if (error) throw error;
            setProducts(data);
        } catch (error) {
            console.error(error);
            toast({ title: 'Error', description: 'No se pudieron cargar los productos', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (data) => {
        try {
            let error;
            // Filtrar campos que no pertenecen a la tabla producto_detalle (id, categoria_id)
            const { id, categoria_id, ...payload } = data;

            if (editingItem) {
                const { error: err } = await supabase.from('producto_detalle').update(payload).eq('id', editingItem.id);
                error = err;
            } else {
                const { error: err } = await supabase.from('producto_detalle').insert(payload);
                error = err;
            }

            if (error) throw error;

            toast({ title: 'Éxito', description: 'Producto guardado correctamente', className: 'bg-green-600 text-white' });
            setIsModalOpen(false);
            setEditingItem(null);
            loadData(); // Recargar para ver cambios (incluyendo joins)
        } catch (error) {
            console.error(error);
            toast({ title: 'Error', description: 'Error al guardar producto', variant: 'destructive' });
        }
    };

    const handleDelete = (id) => {
        setConfirmConfig({
            isOpen: true,
            title: '¿Eliminar Producto?',
            message: '¿Estás seguro de que deseas eliminar este detalle de producto? Esta acción no se puede deshacer.',
            onConfirm: () => executeDelete(id)
        });
    };

    const executeDelete = async (id) => {
        const { error } = await supabase.from('producto_detalle').delete().eq('id', id);
        if (!error) {
            setProducts(products.filter(p => p.id !== id));
            toast({ title: 'Eliminado', className: 'bg-green-600 text-white' });
        } else {
            toast({ title: 'Error', description: 'No se pudo eliminar el producto', variant: 'destructive' });
        }
    };

    // Filter Logic
    const filteredProducts = products.filter(p => {
        const search = searchTerm.toLowerCase();
        return (
            p.producto?.producto?.toLowerCase().includes(search) ||
            p.color?.color?.toLowerCase().includes(search) ||
            p.imagen?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                        <ArchiveIcon /> Gestor de Productos
                    </h2>
                    <p className="text-zinc-500 text-sm">Administra las variaciones y fotos de tus productos</p>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <button onClick={loadData} className="flex-1 sm:flex-none p-2 bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 rounded-lg text-zinc-600 dark:text-zinc-400 hover:text-black dark:hover:text-white transition-colors flex items-center justify-center"><RefreshCw size={20} /></button>
                    <button
                        onClick={() => { setEditingItem(null); setIsModalOpen(true); }}
                        className="flex-[4] sm:flex-none bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-red-900/20 active:scale-95"
                    >
                        <Plus size={18} /> Nuevo Producto
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="bg-white dark:bg-zinc-900/50 p-4 rounded-lg border border-gray-200 dark:border-zinc-800 flex items-center gap-3 shadow-sm dark:shadow-none transition-colors">
                <Search className="text-zinc-400" size={20} />
                <input
                    type="text"
                    placeholder="Buscar por nombre, color o archivo de imagen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-transparent border-none focus:outline-none text-zinc-900 dark:text-white w-full placeholder-zinc-400"
                />
            </div>

            {/* Dashboard grid adjustment */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <AnimatePresence>
                    {filteredProducts.map((item) => (
                        <PublicProductCard
                            key={item.id}
                            item={item}
                            onEdit={(i) => { setEditingItem(i); setIsModalOpen(true); }}
                            onDelete={handleDelete}
                        />
                    ))}
                </AnimatePresence>
            </div>

            {/* Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            onClick={() => setIsModalOpen(false)}
                            className="absolute inset-0 bg-black/50 dark:bg-black/80 backdrop-blur-sm transition-colors"
                        />
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="relative w-full max-w-4xl bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-2xl shadow-2xl p-6 overflow-hidden max-h-[90vh] overflow-y-auto transition-colors"
                        >
                            <h3 className="text-xl font-bold text-zinc-900 dark:text-white mb-6">
                                {editingItem ? 'Editar Producto' : 'Nuevo Producto'}
                            </h3>
                            <ProductForm
                                item={editingItem}
                                catalogs={catalogs}
                                onSave={handleSave}
                                onCancel={() => setIsModalOpen(false)}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Global Confirm Modal for ProductManager */}
            <ConfirmModal
                isOpen={confirmConfig.isOpen}
                onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
            />
        </div>
    );
};

// Icon fix
import { Archive as ArchiveIcon } from 'lucide-react';

export default ProductManager;
